import {element, icon} from './remote-data';
import {localized, safeURL, plainText, bookmarkThumbnail} from './api-data';
import {setText} from './bookmark-render';
import {currentLocale} from './preferences';

// The UI supports two locales; keep a fixed set of formatters across renders.
const dateFormats = Object.fromEntries(['en-US', 'zh-TW'].map(locale => [locale, {
    short: new Intl.DateTimeFormat(locale === 'en-US' ? 'en-GB' : locale, {month: locale === 'en-US' ? 'short' : 'numeric', day: locale === 'en-US' ? '2-digit' : 'numeric', timeZone: 'UTC'}),
    long: new Intl.DateTimeFormat(locale, {dateStyle: 'long', timeZone: 'UTC'}),
    month: new Intl.DateTimeFormat(locale, {month: 'long', timeZone: 'UTC'}),
    period: new Intl.DateTimeFormat(locale, {month: 'long', year: 'numeric', timeZone: 'UTC'})
}]));
export function bookmarkDateLabel(day, style = 'short') {
    const locale = currentLocale();
    const label = dateFormats[locale][style].format(new Date(day));
    return style === 'short' && locale === 'en-US' ? label.replace('Sept', 'Sep').toUpperCase() : label;
}
const cardState = new WeakMap();
function bookmarkMedia(item, section) {
    const media = element('div', 'bookmark-media');
    media.setAttribute('aria-hidden', 'true');
    const thumbnail = bookmarkThumbnail(item, section.dataset.api || 'https://bookmarks.tiwb.me');
    function fallback() {
        media.dataset.mediaKind = 'fallback';
        media.replaceChildren(element('span', 'bookmark-media-fallback'));
    }
    if (!thumbnail) { fallback(); return media; }
    const img = element('img');
    img.alt = ''; img.loading = 'lazy'; img.decoding = 'async'; img.referrerPolicy = 'no-referrer';
    img.width = thumbnail.width; img.height = thumbnail.height;
    media.dataset.mediaKind = thumbnail.kind;
    img.addEventListener('error', fallback, {once: true});
    img.src = thumbnail.url;
    media.append(img);
    return media;
}
export function bookmarkCard(item, day, section, withMedia = false, card) {
    const locale = currentLocale();
    let state = card && cardState.get(card);
    if (state?.item === item && state.day === day && state.locale === locale) return card;
    if (!state) {
        card = element('a', `bookmark-card${withMedia ? ' bookmark-card-media' : ''}`);
        card.target = '_blank'; card.rel = 'noopener noreferrer';
        const source = element('div', 'bookmark-source');
        const hostname = element('span');
        source.append(hostname, icon(section.dataset.externalIcon, 12));
        const title = element('h3', 'bookmark-title');
        const summary = element('p', 'bookmark-description');
        const meta = element('p', 'bookmark-meta');
        const time = element('time');
        const tag = document.createTextNode('');
        meta.append(time, tag);
        const content = withMedia ? element('div', 'bookmark-card-content') : card;
        const text = withMedia ? element('div', 'bookmark-copy') : card;
        text.append(source, title, summary);
        if (withMedia) { content.append(text); card.append(content); }
        card.append(meta);
        state = {hostname, title, summary, time, tag, content};
        cardState.set(card, state);
    }
    const copy = localized(item, locale);
    if (state.item !== item) {
        card.href = safeURL(item.url);
        card.dataset.savedDate = day; card.dataset.bookmarkId = item.id;
        setText(state.hostname, new URL(item.url).hostname.replace(/^www\./, ''));
        state.time.dateTime = item.saved_at;
    }
    card.lang = locale;
    setText(state.title, plainText(copy.title || item.title));
    setText(state.summary, plainText(copy.summary || item.summary));
    setText(state.time, bookmarkDateLabel(day));
    setText(state.tag, copy.tags?.[0] ? ` · ${plainText(copy.tags[0])}` : '');
    if (withMedia && state.item !== item) {
        const thumbnail = bookmarkThumbnail(item, section.dataset.api || 'https://bookmarks.tiwb.me');
        const mediaKey = JSON.stringify(thumbnail);
        if (!state.media || state.mediaKey !== mediaKey) {
            const media = bookmarkMedia(item, section);
            if (state.media) state.media.replaceWith(media);
            else state.content.append(media);
            state.media = media; state.mediaKey = mediaKey;
        }
    }
    Object.assign(state, {item, day, locale});
    return card;
}
